import { createContext } from "@lit/context";
import { CustomerDtoWritable, AddOrUpdateProductDetailDto, OrderDtoReadable, OrderLineDto, SessionLineDto, OrderService, PaymentService, AddOrUpdateCustomerInformationDto } from "../api";
import { BasketDateLine, BasketTimeSlotLine } from "../cart.types";
import { BehaviorSubject, Subject } from "rxjs";
import dayjs from "dayjs";
import { StagingCartItem } from "../types";
import { isSessionLineDto } from "../core/helpers";

 export const orderManagementContextKey = createContext<OrderManagementContext>("order-management-context");

export class OrderManagementContext {

    private timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    private orderState = new BehaviorSubject<OrderDtoReadable | null>(null);
    private basketLines = new BehaviorSubject<BasketDateLine[]>([]);

    public orderState$ = this.orderState.asObservable();
    public basketLines$ = this.basketLines.asObservable();
    public basketItemAdded$ = new Subject<void>();
    public loadingChanged$ = new Subject<boolean>();

    constructor(initialOrderState: OrderDtoReadable, timeZone?: string) {
        console.log("Initializing OrderManagementContext with initial order state:", initialOrderState);
        if (timeZone) {
            this.timeZone = timeZone;
        }
        this.processOrderState(initialOrderState);
    }

    public init(timeZone: string) {
        this.timeZone = timeZone;
    }

    public getOrderLine(sessionStart : dayjs.Dayjs, sessionEnd: dayjs.Dayjs, variantId: string): OrderLineDto | undefined {
        return this.orderState.value?.orderLines?.find(line => {
                        if(!isSessionLineDto(line)) {
                            return false;
                        }
                        const lineSessionStart = dayjs.utc(line.start).tz(this.timeZone);
                        const lineSessionEnd = dayjs.utc(line.end).tz(this.timeZone);
                        return line.productVariantId === variantId
                            && lineSessionStart.isSame(sessionStart)
                            && lineSessionEnd.isSame(sessionEnd);
        });
    }


    public async getCart() {
        const { data, error } = await OrderService.getCurrentOrder();

        if (error) {
            console.error("Failed to fetch cart:", error);
            return;
        }

        if (data) {
            this.orderState.next(data);
            this.processOrderState(data);
        }
    }

    public async addOrUpdateOrderLines(stagingCartItems: StagingCartItem[]) {
        this.loadingChanged$.next(true);
        const toUpdate = stagingCartItems.filter(item =>
            item.orderLineId === undefined ||
            this.orderState?.value?.orderLines?.find(line => line.id === item.orderLineId)?.quantity !== item.quantity
        );

        const details = toUpdate.map<AddOrUpdateProductDetailDto>(item => ({
            orderLineId: item.orderLineId,
            date: item.date.format("YYYY-MM-DD"),
            productVariantId: item.productVariant.id,
            timeSlotId: item.timeSlotId,
            quantity: item.quantity,
        }));

        const { data, error } = await OrderService.addOrUpdateProducts({
            body: {
                details: details,
            }
        });

        if (error) {
            console.error("Failed to update cart items:", error);
            this.loadingChanged$.next(false);
            return;
        }

        if (data) {
            await this.getCart();
        } else {
            console.error("Failed to update cart items: No data returned", error);
            // TODO: Notify error
            this.loadingChanged$.next(false);
            return;
        }

        this.basketItemAdded$.next();
        this.loadingChanged$.next(false);
    }

    public async removeProduct(basketDetailLine: BasketTimeSlotLine) {
        this.loadingChanged$.next(true);
        const { data, error } = await OrderService.bulkDeleteOrderLines({
            body: {
                orderLineIds: basketDetailLine.orderLines.map(line => line.id!),
            }
        });

        if (error) {
            console.error("Failed to remove product:", error);
            this.loadingChanged$.next(false);
            return;
        }

        if (data) {
            await this.getCart();
            if(this.orderState.value?.orderLines?.length === 0) {
                window.location.href = "/";
            }
        } else {
            console.error("Failed to remove product: No data returned");
            // TODO: Notify error
            this.loadingChanged$.next(false);
            return;
        }

        this.loadingChanged$.next(false);
    }

    public async addCustomerDetails(customerDetails: Partial<AddOrUpdateCustomerInformationDto>) {
        this.loadingChanged$.next(true);
        const nextValue = {
            ...this.orderState.value?.customer,
            ...customerDetails,
        } as AddOrUpdateCustomerInformationDto;

        const { data, error } = await OrderService.updateCustomerDetails({
            body: nextValue,
        });

        if (data) {
            await this.getCart();
        } else {
            console.error("Failed to add customer details: No data returned", error);
            this.loadingChanged$.next(false);
            return false;
        }

        this.loadingChanged$.next(false);
        return true;
    }

    public async applyDiscountCode(discountCode: string) : Promise<boolean>  {
        this.loadingChanged$.next(true);
        const { data, error } = await OrderService.applyDiscountCode({
            query: {
                code: discountCode,
            }
        });

        if (error) {
            console.error("Failed to apply discount code:", error);
            this.loadingChanged$.next(false);
            return false;
        }

        if (data) {
            await this.getCart();
        } else {
            console.error("Failed to apply discount code: No data returned");
            this.loadingChanged$.next(false);
            return false;
        }

        this.loadingChanged$.next(false);
        return true;
    }
    public async clearDiscountCode() : Promise<boolean> {
        this.loadingChanged$.next(true);
        const { data, error } = await OrderService.applyDiscountCode({
            query: {
                code: "",
            }
        });

        if (error) {
            console.error("Failed to clear discount code:", error);
            this.loadingChanged$.next(false);
            return false;
        }

        if (data) {
            await this.getCart();
        } else {
            console.error("Failed to clear discount code: No data returned");
            this.loadingChanged$.next(false);
            return false;
        }

        this.loadingChanged$.next(false);
        return true;
    }

    public async getPaymentRedirect() : Promise<void> {
        const { data, error } = await PaymentService.createTransactionToken();
        if (error || !data || !data.paymentProviderToken) {
            console.error("Error getting payment token:", error);
            return;
        }


        window.location.href = data.paymentProviderToken;
    }

    private processOrderState(orderState: OrderDtoReadable) {
        console.log("Processing order state:", orderState);
        this.orderState.next(orderState);

        if(!orderState.orderLines) {
            this.basketLines.next([]);
            return;
        }

        const nextBasketLines = orderState?.orderLines?.reduce<BasketDateLine[]>((basketDateLinesOut, orderLine) => {
                if(isSessionLineDto(orderLine) === false || !orderLine.productName) {
                    return basketDateLinesOut;
                }
                const sessionDate = dayjs.utc(orderLine.start).tz(this.timeZone);

                let basketDateLine = basketDateLinesOut.find(line=>line.date.isSame(sessionDate, "day"));
                if(basketDateLine === undefined) {
                    basketDateLine = {
                        date: sessionDate.startOf("day"),
                        details: [],
                    };
                    basketDateLinesOut.push(basketDateLine);
                }

                let basketLineDetail = basketDateLine.details.find(detail => detail.product === orderLine.productName && detail.date.isSame(sessionDate));
                if(basketLineDetail === undefined) {
                    basketLineDetail = {
                        date: sessionDate,
                        product: orderLine.productName,
                        orderLines: [orderLine],
                    };

                    basketDateLine.details.push(basketLineDetail);
                } else {
                    basketLineDetail.orderLines.push(orderLine);
                }
                return basketDateLinesOut;
            }, []);


        nextBasketLines.forEach((basketLine) => {
            basketLine.details = basketLine.details.sort((a, b) => {
                const nameComparison = a.product.localeCompare(b.product);
                if(nameComparison === 0) {
                    return a.date.isBefore(b.date) ? -1 : 1;
                }
                return nameComparison;
            });
        });

        this.basketLines.next(nextBasketLines);
    }
}